/**
 * Shared page shell for Communications channel sub-apps.
 *
 * Renders a consistent header (icon + title + subtitle) over the page content,
 * wrapped in the standard LayoutContainer + FadeIn. Used by the per-channel page files
 * (Email/SMS/Push; WhatsApp has its own copy for historical reasons).
 *
 * LayoutContainer — not a bare Container — because it is the only thing that reads the user's
 * Layout Preferences (width + horizontal padding). A hardcoded `<Container size="xl">` silently
 * ignores both settings.
 */

import { ReactNode } from 'react';
import { IconArrowLeft } from '@tabler/icons-react';
import { Stack, Group, Text, Title, Button } from '../../../../components/ui';
import { LayoutContainer } from '../../../../components/ui/layout';
import { Card } from '../../../../components/ui/data-display';
import { FadeIn } from '../../../../components/animations/transitions';

export function ChannelPage({
  icon,
  title,
  subtitle,
  actions,
  onBack,
  backLabel = 'Back',
  children,
}: {
  icon: ReactNode;
  title: string;
  subtitle?: string;
  actions?: ReactNode;
  /** When set, renders a Back button above the title (turns the page into a detail view). */
  onBack?: () => void;
  backLabel?: string;
  children: ReactNode;
}) {
  return (
    <LayoutContainer py="xl">
      <FadeIn delay={80}>
        <Stack gap="lg">
          {onBack && (
            <Button variant="subtle" size="xs" px={4} leftSection={<IconArrowLeft size={16} />} onClick={onBack} style={{ alignSelf: 'flex-start' }}>
              {backLabel}
            </Button>
          )}
          <Group justify="space-between" align="flex-start">
            <Stack gap={4}>
              <Group gap="sm">
                {icon}
                <Title order={2}>{title}</Title>
              </Group>
              {subtitle && (
                <Text size="sm" c="dimmed">
                  {subtitle}
                </Text>
              )}
            </Stack>
            {actions}
          </Group>
          {children}
        </Stack>
      </FadeIn>
    </LayoutContainer>
  );
}

/** Placeholder for pages whose rich implementation lands in a later phase. */
export function ComingSoon({ feature }: { feature: string }) {
  return (
    <Card withBorder padding="xl">
      <Stack gap="xs">
        <Title order={5}>{feature}</Title>
        <Text size="sm" c="dimmed">
          This area is being built out. The {feature.toLowerCase()} experience will appear here.
        </Text>
      </Stack>
    </Card>
  );
}
